import { cap } from '../utils/format';
import React, { useState, useEffect } from 'react';
import { Shield, Eye, CheckCircle, Clock, FileText, RefreshCw, BookOpen } from 'lucide-react';
import api from '../api/client';
import { PageHeader } from '../components/FilterBar';

export default function MyPolicies() {
  const [policies, setPolicies] = useState([]);
  const [acceptances, setAcceptances] = useState([]);
  const [policyAcks, setPolicyAcks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a, acks] = await Promise.all([
        api.get('/policies').then(r => r.data || []).catch(() => []),
        api.get('/policies/acceptances').then(r => r.data || []).catch(() => []),
        api.get('/policies/my-acknowledgements').then(r => r.data || []).catch(() => []),
      ]);
      setPolicies(p);
      setAcceptances(a);
      setPolicyAcks(acks);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64 gap-3" style={{ color: '#334155' }}>
      <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
      Loading policies...
    </div>
  );

  return (
    <div className="space-y-5">
      <PageHeader title="IT Policies" count={policies.length}>
        <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
      </PageHeader>

      {/* IT Policy Documents */}
      <div className="glass glow-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)' }}>
            <FileText size={14} style={{ color: '#818cf8' }}/>
          </div>
          <span className="font-semibold text-sm" style={{ color: '#94a3b8' }}>IT Policy Documents</span>
        </div>
        {policies.length === 0 ? (
          <div className="py-6 text-center text-sm" style={{ color: '#1e3a5f' }}>No IT policy documents uploaded yet.</div>
        ) : (
          <div className="space-y-2">
            {policies.map((p, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.2)' }}>
                  <Shield size={15} style={{ color: '#818cf8' }}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>{p.name}</div>
                  {p.type && (
                    <div className="text-xs mt-0.5 capitalize" style={{ color: '#475569' }}>{p.type.replace(/_/g, ' ')} Policy</div>
                  )}
                  {p.created_at && (
                    <div className="text-xs mt-0.5" style={{ color: '#334155' }}>
                      Published: {new Date(p.created_at).toLocaleDateString('en-GB')}
                    </div>
                  )}
                </div>
                <a
                  href={`/api/policies/view/${p.id}?token=${localStorage.getItem('token')}#toolbar=0&navpanes=0`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5 flex-shrink-0"
                  style={{ color: '#60a5fa' }}>
                  <Eye size={12}/>
                  View
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* My Policy Acknowledgements */}
      <div className="glass glow-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)' }}>
            <BookOpen size={14} style={{ color: '#818cf8' }}/>
          </div>
          <span className="font-semibold text-sm" style={{ color: '#94a3b8' }}>My IT Policy Acknowledgements</span>
          <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', color: '#475569' }}>
            {policyAcks.filter(a => a.status === 'acknowledged').length} acknowledged
          </span>
        </div>
        {policyAcks.length === 0 ? (
          <div className="py-6 text-center text-sm" style={{ color: '#1e3a5f' }}>No policy acknowledgement records found.</div>
        ) : (
          <div className="space-y-2">
            {policyAcks.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>
                    {a.policy?.name || a.policy_name || '—'}
                  </div>
                  <div className="text-xs mt-0.5 capitalize" style={{ color: '#475569' }}>
                    {(a.policy_type || '').replace(/_/g, ' ')} Policy
                  </div>
                  {a.requested_at && (
                    <div className="text-xs mt-0.5" style={{ color: '#334155' }}>
                      Requested: {new Date(a.requested_at).toLocaleDateString('en-GB')}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  {a.status === 'acknowledged' ? (
                    <div className="flex flex-col items-end gap-0.5">
                      <div className="flex items-center gap-1.5 text-xs" style={{ color: '#34d399' }}>
                        <CheckCircle size={12}/>
                        Acknowledged
                      </div>
                      {a.acknowledged_at && (
                        <div className="text-xs" style={{ color: '#334155' }}>
                          {new Date(a.acknowledged_at).toLocaleDateString('en-GB')}{' '}
                          {new Date(a.acknowledged_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs" style={{ color: '#f59e0b' }}>
                      <Clock size={12}/>
                      Pending
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* My Handover Acceptances */}
      <div className="glass glow-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}>
            <CheckCircle size={14} style={{ color: '#34d399' }}/>
          </div>
          <span className="font-semibold text-sm" style={{ color: '#94a3b8' }}>My Asset Handover Acceptances</span>
          <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', color: '#475569' }}>{acceptances.length}</span>
        </div>
        {acceptances.length === 0 ? (
          <div className="py-6 text-center text-sm" style={{ color: '#1e3a5f' }}>
            No handover acceptance records found. Your IT team will send you a link when you receive new equipment.
          </div>
        ) : (
          <div className="space-y-2">
            {acceptances.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {a.asset?.asset_number && (
                      <span className="font-mono text-xs font-semibold" style={{ color: '#60a5fa' }}>{a.asset.asset_number}</span>
                    )}
                    {a.asset && (
                      <span className="text-xs" style={{ color: '#475569' }}>{a.asset.brand} {a.asset.model}</span>
                    )}
                  </div>
                  <div className="text-xs mt-1" style={{ color: '#334155' }}>
                    Received: {a.created_at ? new Date(a.created_at).toLocaleDateString('en-GB') : '—'}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {a.accepted_at ? (
                    <div className="flex flex-col items-end gap-0.5">
                      <div className="flex items-center gap-1.5 text-xs" style={{ color: '#34d399' }}>
                        <CheckCircle size={12}/>
                        Acknowledged
                      </div>
                      <div className="text-xs" style={{ color: '#334155' }}>
                        {new Date(a.accepted_at).toLocaleDateString('en-GB')} {new Date(a.accepted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs" style={{ color: '#f59e0b' }}>
                      <Clock size={12}/>
                      Pending
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="rounded-xl p-4 text-xs" style={{ background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.15)', color: '#475569' }}>
        <strong style={{ color: '#60a5fa' }}>Note:</strong> You can view IT policy documents at any time from this page. When you receive new IT equipment, the IT department will send you an email with an acceptance link to confirm receipt.
      </div>
    </div>
  );
}
